import { Injectable } from '@angular/core';
import { HttpClient, HttpEvent, HttpEventType, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, defer } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { isIosNative } from 'src/app/commons/utils/platform';

/** One moment of an upload (UPLOAD_PROGRESS_PLAN.md, 2026-09-29).
 *  - uploading:  bytes are still going up — loaded / total / pct / speed / time left are real.
 *  - processing: every byte is sent, the server is still working (resize, storage, AI reading).
 *  - done:       the server answered; `body` is its reply. */
export interface UploadProgress<T = any> {
  state: 'uploading' | 'processing' | 'done';
  loaded: number;
  total: number;
  /** 0–100 */
  pct: number;
  /** Bytes per second (smoothed). 0 until a speed is known. */
  speedBps: number;
  /** Seconds left at the current speed; null when unknown. */
  etaSec: number | null;
  /** The real byte count can't be known (iPhone app: its requests go through the native network layer,
   *  which reports only "sent" and the final answer) — show a moving "Sending…" bar, never a frozen 0%. */
  indeterminate?: boolean;
  body?: T;
}

/**
 * Turns the HttpClient events of ONE upload into UploadProgress (speed smoothed, time left, the
 * "processing" phase once every byte is sent). Used by UploadProgressService.post() and directly by
 * screens that already read HttpEvents themselves — so every upload shows the same numbers.
 * One tracker per upload (it keeps that upload's speed state).
 */
export class UploadProgressTracker {
  private lastAt = 0;
  private lastLoaded = 0;
  private speed = 0;

  constructor(private total = 0) {}

  update<T = any>(ev: HttpEvent<T>): UploadProgress<T> | null {
    switch (ev.type) {
      case HttpEventType.Sent:
        this.lastAt = performance.now();
        return { state: 'uploading', loaded: 0, total: this.total, pct: 0, speedBps: 0, etaSec: null,
                 ...(isIosNative() ? { indeterminate: true } : {}) };
      case HttpEventType.UploadProgress: {
        const now = performance.now();
        if (!this.lastAt) this.lastAt = now;
        if (ev.total) this.total = ev.total;
        const dt = (now - this.lastAt) / 1000;
        if (dt > 0.15) {
          const inst = (ev.loaded - this.lastLoaded) / dt;
          this.speed = this.speed ? this.speed * 0.7 + inst * 0.3 : inst; // smoothed so the numbers don't jump
          this.lastAt = now;
          this.lastLoaded = ev.loaded;
        }
        const total = this.total;
        const pct = total > 0 ? Math.min(100, Math.round((ev.loaded / total) * 100)) : 0;
        const sentAll = total > 0 && ev.loaded >= total;
        return {
          state: sentAll ? 'processing' : 'uploading',
          loaded: ev.loaded, total, pct: sentAll ? 100 : pct, speedBps: this.speed,
          etaSec: !sentAll && this.speed > 0 ? Math.max(0, Math.round((total - ev.loaded) / this.speed)) : null,
        };
      }
      case HttpEventType.Response:
        return { state: 'done', loaded: this.total, total: this.total, pct: 100, speedBps: this.speed, etaSec: 0, body: ev.body as T };
      default:
        return null; // headers / download progress: nothing to show
    }
  }
}

/**
 * Shared upload helper: posts a FormData and reports REAL progress (reportProgress + observe 'events'),
 * never a fake timer. Screens keep their own endpoints — only the way they send and show the upload
 * changes. Unsubscribing aborts the request (that is how "Cancel" works).
 */
@Injectable({ providedIn: 'root' })
export class UploadProgressService {
  constructor(private http: HttpClient) {}

  post<T = any>(url: string, body: FormData,
                options: { params?: HttpParams; headers?: HttpHeaders; withCredentials?: boolean } = {}): Observable<UploadProgress<T>> {
    return defer(() => {
      const tracker = new UploadProgressTracker(UploadProgressService.formDataSize(body));
      return this.http.post<T>(url, body, { ...options, reportProgress: true, observe: 'events' }).pipe(
        map((ev: HttpEvent<T>) => tracker.update<T>(ev)),
        filter((p): p is UploadProgress<T> => p !== null),
      );
    });
  }

  /** Rough size of the files in a FormData (used before the browser reports the real total). */
  static formDataSize(fd: FormData): number {
    let size = 0;
    fd.forEach(v => { if (v instanceof Blob) size += v.size; });
    return size;
  }
}
